import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import classNames from 'classnames';

import { ButtonPrimary } from 'src/components/Button';
import Chips from 'src/components/Chips';
import Divider from 'src/components/Divider';
import RelatedGameList from 'src/components/GameDetail/RelatedGameList';
import Review from 'src/components/GameDetail/Review';
import Screenshot from 'src/components/GameDetail/Screenshot';
import ImageC from 'src/components/Image';
import ModalPlayGame from 'src/components/ModalPlayGame';
import Skeleton from 'src/components/Skeleton';
import StarRate from 'src/components/StarRate';
import { useGetGameList, useGetGameMarket } from 'src/hooks';
import { IGame, IGameDetail, IGameMarketData } from 'src/interface';
import { formatDate, formatDateHowLongAgo } from 'src/utils/func/format-date';
import { numberWithThousandSeparator } from 'src/utils/func/format-number';
import useDeviceSize from 'src/utils/hooks/useDeviceSize';

import classes from './index.module.less';

const GameDetail = ({ data }: { data: IGameDetail }) => {
	const deviceSize = useDeviceSize();

	const router = useRouter();
	const { slug } = router.query;

	const [openModalPlayGame, setOpenModalPlayGame] = useState<boolean>(false);

	const dataGameDetail = data;

	const isLoadingGameDetail = !router.isReady;

	const { data: dataMarket, isFetching: isLoadingGameMarketData } = useGetGameMarket(
		dataGameDetail?.coin_gecko_api || '',
	);
	const dataGameMarket: IGameMarketData = dataMarket?.data?.[0];

	const { data: dataG, isLoading: isLoadingGames } = useGetGameList({
		relatedWith: slug,
		limit: 10,
	});
	const dataGames: IGame[] = dataG?.data?.data?.slice(0, 2);

	return (
		<div className={classes['game-detail-container']}>
			{isLoadingGameDetail ? (
				<Skeleton className={classes.banner} width="100%" height="35%" />
			) : (
				<div className={classes.banner}>
					<ImageC
						alt={`banner ${dataGameDetail?.name}`}
						className={classes.img}
						src={dataGameDetail?.main_image_url}
					/>
				</div>
			)}

			<div className="container mb-4">
				{/* <div className="container sticky" style={{ top: 84, zIndex: 1 }}> */}
				<div className={classes.header}>
					<Row gutter={[30, 15]}>
						<Col xs={24} md={10}>
							{isLoadingGameDetail ? (
								<>
									<Skeleton variant="text" className="mb-2" width="100%" height="36px" />
									<Skeleton variant="text" width="100%" height="20px" />
								</>
							) : (
								<>
									<h1>{dataGameDetail?.name}</h1>
									<label>{dataGameDetail?.developer}</label>
								</>
							)}
						</Col>
						<Col xs={24} md={8}>
							<div className={classes.data}>
								{isLoadingGameDetail ? (
									<div>
										{deviceSize !== 'mobile' && <Skeleton variant="text" height={16} width={60} />}
										<Skeleton
											className="mt-1"
											height={deviceSize !== 'mobile' ? 24 : 20}
											width={deviceSize !== 'mobile' ? 24 : 20}
										/>
									</div>
								) : (
									<div>
										{deviceSize !== 'mobile' && <label className="fs-16 fw-400 mb-1">Chain</label>}
										<div className="d-flex" style={{ gap: 6 }}>
											{dataGameDetail?.chains?.map((c) => (
												<ImageC
													key={c.chain_id}
													alt={`icon ${c.name}`}
													width={deviceSize !== 'mobile' ? 24 : 20}
													height={deviceSize !== 'mobile' ? 24 : 20}
													src={c.image_url}
												/>
											))}
										</div>
									</div>
								)}
								{isLoadingGameDetail ? (
									<div>
										{deviceSize !== 'mobile' && <Skeleton variant="text" height={16} width={60} />}
										<Skeleton
											className="mt-1"
											height={deviceSize !== 'mobile' ? 24 : 20}
											width={deviceSize !== 'mobile' ? 24 : 20}
										/>
									</div>
								) : (
									<div>
										{deviceSize !== 'mobile' && (
											<label className="fs-16 fw-400 mb-1">Platform</label>
										)}
										<div className="d-flex" style={{ gap: 10 }}>
											{dataGameDetail?.platform?.toLowerCase() === 'coming soon' ? (
												<span>Coming Soon</span>
											) : (
												dataGameDetail?.platform
													?.split(',')
													?.map((p) => (
														<ImageC
															key={p}
															alt={`icon ${p}`}
															width={deviceSize !== 'mobile' ? 24 : 20}
															height={deviceSize !== 'mobile' ? 24 : 20}
															src={`/icons/icon-platform-${p.toLowerCase()}.svg`}
														/>
													))
											)}
										</div>
									</div>
								)}
								{isLoadingGameDetail ? (
									<div>
										{deviceSize !== 'mobile' && <Skeleton variant="text" height={16} width={60} />}
										<Skeleton
											className="mt-1"
											height={deviceSize !== 'mobile' ? 24 : 20}
											width={deviceSize !== 'mobile' ? 24 : 20}
										/>
									</div>
								) : (
									<div>
										{deviceSize !== 'mobile' && <label className="fs-16 fw-400 mb-1">Rating</label>}
										<StarRate className={classes.rating} ratingNumber={dataGameDetail?.rating} />
									</div>
								)}
							</div>
						</Col>
						<Col xs={24} md={6}>
							{isLoadingGameDetail ? (
								<Skeleton height={60} borderRadius="4px" />
							) : (
								<React.Fragment>
									<ButtonPrimary
										className={classes['play-game-btn']}
										disabled={dataGameDetail?.status === 'coming-soon'}
										onClick={() => setOpenModalPlayGame(true)}
									>
										{dataGameDetail?.status === 'coming-soon' ? 'Coming Soon' : 'Play Game'}
									</ButtonPrimary>
									<ModalPlayGame
										open={openModalPlayGame}
										onClose={() => setOpenModalPlayGame(false)}
										data={dataGameDetail}
									/>
								</React.Fragment>
							)}
						</Col>
					</Row>
				</div>
			</div>

			<div className="container">
				<Row gutter={30}>
					<Col span={24}>
						<Screenshot data={dataGameDetail?.medias} isLoading={isLoadingGameDetail} />
					</Col>

					<Col span={24}></Col>

					<Col span={24} className="mb-5">
						<Row gutter={30}>
							<Col
								xs={24}
								md={14}
								className={classNames({
									[classes['border-right']]: deviceSize !== 'mobile',
								})}
							>
								{isLoadingGameDetail ? (
									new Array(5)
										.fill({ id: 'loading' })
										?.map((_, index) => (
											<Skeleton key={index} variant="text" className="mb-2" height={20} />
										))
								) : (
									<div className={classes.description}>{dataGameDetail?.description}</div>
								)}
							</Col>
							<Col xs={24} md={10}>
								{isLoadingGameDetail ? (
									<Skeleton variant="text" className="mb-2" height={20} width={90} />
								) : (
									<label className="fs-20 fw-400">Category</label>
								)}
								<div className="d-flex flex-wrap mt-2" style={{ gap: 10 }}>
									{isLoadingGameDetail
										? new Array(3)
												.fill({ id: 'loading' })
												?.map((_, index) => (
													<Skeleton key={index} className="mb-2" height={26} width={78} />
												))
										: dataGameDetail?.tag_names?.split(', ')?.map((tag) => (
												// eslint-disable-next-line react/jsx-indent
												<Chips key={tag} className="fs-14 fs-md-20">
													{tag}
												</Chips>
											))}
								</div>
							</Col>
						</Row>
					</Col>

					<Col span={24} className="mb-5">
						<Row gutter={30} className="justify-content-between">
							<Col xs={24} md={10}>
								<div className="mb-4">
									<h2>About</h2>
									<Divider
										className="mt-2 mb-3"
										width={deviceSize !== 'mobile' ? 4 : 2}
										color="#2B2B2B"
									/>
									{isLoadingGameDetail ? (
										new Array(4)
											.fill({ id: 'loading' })
											?.map((_, index) => (
												<Skeleton key={index} variant="text" className="mb-2" height={20} />
											))
									) : (
										<div className={classes['about-content']}>
											<table>
												<tbody>
													<tr>
														<td className="pr-4">Release Date:</td>
														<td>
															{dataGameDetail?.released_at
																? formatDate(dataGameDetail?.released_at, 'D MMM, YYYY')
																: 'Unknown'}
														</td>
													</tr>
													<tr>
														<td>Developer:</td>
														<td>{dataGameDetail?.developer}</td>
													</tr>
													<tr>
														<td>Publisher:</td>
														<td>{dataGameDetail?.publisher}</td>
													</tr>
													<tr>
														<td>Status:</td>
														<td style={{ textTransform: 'capitalize' }}>
															{dataGameDetail?.status}
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									)}
								</div>

								<h2>Community</h2>
								<Divider
									className="mt-2 mb-3"
									width={deviceSize !== 'mobile' ? 4 : 2}
									color="#2B2B2B"
								/>
								<div className={classes['community-list']}>
									{isLoadingGameDetail ? (
										new Array(4)
											.fill({ id: 'loading' })
											?.map((_, index) => (
												<Skeleton key={index} className="mb-2" height={52} width={52} />
											))
									) : (
										<>
											{dataGameDetail?.official_site_link && (
												<Link
													href={dataGameDetail?.official_site_link}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ImageC
														alt={'icon website'}
														width={deviceSize !== 'mobile' ? 52 : 34}
														height={deviceSize !== 'mobile' ? 52 : 34}
														src="/icons/website.svg"
													/>
												</Link>
											)}
											{dataGameDetail?.discord_link && (
												<Link
													href={dataGameDetail?.discord_link}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ImageC
														alt={'icon discord'}
														width={deviceSize !== 'mobile' ? 52 : 34}
														height={deviceSize !== 'mobile' ? 52 : 34}
														src="/icons/discord-fill.svg"
													/>
												</Link>
											)}
											{dataGameDetail?.facebook_link && (
												<Link
													href={dataGameDetail?.facebook_link}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ImageC
														alt={'icon facebook'}
														width={deviceSize !== 'mobile' ? 52 : 34}
														height={deviceSize !== 'mobile' ? 52 : 34}
														src="/icons/facebook.svg"
													/>
												</Link>
											)}
											{dataGameDetail?.instagram_link && (
												<Link
													href={dataGameDetail?.instagram_link}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ImageC
														alt={'icon instagram'}
														width={deviceSize !== 'mobile' ? 52 : 34}
														height={deviceSize !== 'mobile' ? 52 : 34}
														src="/icons/instagram-fill.svg"
													/>
												</Link>
											)}
										</>
									)}
								</div>
							</Col>
							<Col xs={24} md={12}>
								<h2>Market Data</h2>
								<Divider
									className="mt-2 mb-3"
									width={deviceSize !== 'mobile' ? 4 : 2}
									color="#2B2B2B"
								/>
								{isLoadingGameDetail || isLoadingGameMarketData ? (
									<>
										<Skeleton className="mb-2" height={158} />
										<Skeleton variant="text" className="mb-2" height={20} />
										<Skeleton variant="text" className="mb-2" height={20} />
										<Skeleton variant="text" className="mb-2" height={20} />
									</>
								) : !dataGameMarket ? (
									'No Data Found'
								) : (
									<div className={classes['market-data']}>
										<div className={classes.highlight}>
											<div className="fs-20">Current price (24H)</div>
											<div
												className="d-flex flex-wrap align-items-center"
												style={{ columnGap: 23 }}
											>
												<div className={classes.price}>
													${numberWithThousandSeparator(dataGameMarket?.current_price)}
												</div>
												<div
													className={classNames(classes.percentage, {
														'text-success': dataGameMarket?.price_change_percentage_24h >= 0,
														'text-danger': dataGameMarket?.price_change_percentage_24h < 0,
													})}
												>
													{numberWithThousandSeparator(dataGameMarket?.price_change_percentage_24h)}
													%
												</div>
											</div>
											<div className="fs-20">
												{dataGameMarket?.name?.toUpperCase()} (
												{dataGameMarket?.symbol?.toUpperCase()})
											</div>
										</div>
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												Market Cap:
											</Col>
											<Col xs={12} lg={14}>
												${numberWithThousandSeparator(dataGameMarket?.market_cap)}
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												Fully Diluted Valuation:
											</Col>
											<Col xs={12} lg={14}>
												${numberWithThousandSeparator(dataGameMarket?.fully_diluted_valuation)}
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												24 Hour Trading Vol:
											</Col>
											<Col xs={12} lg={14}>
												${numberWithThousandSeparator(dataGameMarket?.total_volume)}
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												Circulating supply:
											</Col>
											<Col xs={12} lg={14}>
												{numberWithThousandSeparator(dataGameMarket?.circulating_supply)}
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												Total supply:
											</Col>
											<Col xs={12} lg={14}>
												{numberWithThousandSeparator(dataGameMarket?.total_supply)}
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												24h High / 24h Low:
											</Col>
											<Col xs={12} lg={14}>
												${numberWithThousandSeparator(dataGameMarket?.high_24h)} / $
												{numberWithThousandSeparator(dataGameMarket?.low_24h)}
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												All-Time High:
											</Col>
											<Col xs={12} lg={14}>
												<div className="d-flex flex-wrap" style={{ columnGap: 10 }}>
													<div>${numberWithThousandSeparator(dataGameMarket?.ath)}</div>
													<div
														className={classNames({
															'text-success': dataGameMarket?.ath_change_percentage >= 0,
															'text-danger': dataGameMarket?.ath_change_percentage < 0,
														})}
													>
														{numberWithThousandSeparator(dataGameMarket?.ath_change_percentage)}%
													</div>
												</div>
												<div className={classes.date}>
													{formatDate(dataGameMarket?.ath_date, 'MMM D, YYYY')},{' '}
													{formatDateHowLongAgo(dataGameMarket?.ath_date)}
												</div>
											</Col>
										</Row>
										<Divider className={classes.divider} />
										<Row gutter={30}>
											<Col xs={12} lg={10}>
												All-Time Low:
											</Col>
											<Col xs={12} lg={14}>
												<div className="d-flex flex-wrap" style={{ columnGap: 10 }}>
													<div>${numberWithThousandSeparator(dataGameMarket?.atl)}</div>
													<div
														className={classNames({
															'text-success': dataGameMarket?.atl_change_percentage >= 0,
															'text-danger': dataGameMarket?.atl_change_percentage < 0,
														})}
													>
														{numberWithThousandSeparator(dataGameMarket?.atl_change_percentage)}%
													</div>
												</div>
												<div className={classes.date}>
													{formatDate(dataGameMarket?.atl_date, 'MMM D, YYYY')},{' '}
													{formatDateHowLongAgo(dataGameMarket?.atl_date)}
												</div>
											</Col>
										</Row>
										<Divider className={classes.divider} />
									</div>
								)}
							</Col>
						</Row>
					</Col>

					<Col span={24} className="mb-5">
						<h2>Review</h2>
						<Divider
							className="mt-2 mb-3"
							width={deviceSize !== 'mobile' ? 4 : 2}
							color="#2B2B2B"
						/>
						<Review gameSlug={slug as string} />
					</Col>

					<Col span={24}>
						<h2>Game You Might Interested in</h2>
						<Divider
							className="mt-2 mb-3"
							width={deviceSize !== 'mobile' ? 4 : 2}
							color="#2B2B2B"
						/>
						<Row gutter={30}>
							<Col span={24}>
								<RelatedGameList items={dataGames} isLoading={isLoadingGames} />
							</Col>
						</Row>
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default GameDetail;
